/* eslint-disable react-refresh/only-export-components --
 * This module deliberately pairs the container component with its imperative
 * `showModal` / `showLoading` / `hideLoading` triggers so any module can open a
 * modal without threading props. Splitting them would break that entry point.
 */
import { useEffect, useState } from 'react'
import { CheckCircle, XCircle } from 'lucide-react'
import LoadingSpinner from '@/shared/components/ui/LoadingSpinner'

// ─── Confirmation / result modal ─────────────────────────────────────────────

interface ModalPayload {
  type: 'success' | 'error'
  title: string
  body?: string
}

const modalListeners: Array<(m: ModalPayload | null) => void> = []

export function showModal(type: 'success' | 'error', title: string, body?: string) {
  modalListeners.forEach((fn) => fn({ type, title, body }))
}

// ─── Blocking loading overlay ─────────────────────────────────────────────────

const loadingListeners: Array<(msg: string | null) => void> = []

/** Show a full-screen blocking loading overlay. Call hideLoading() when done. */
export function showLoading(message = 'Loading…') {
  loadingListeners.forEach((fn) => fn(message))
}

export function hideLoading() {
  loadingListeners.forEach((fn) => fn(null))
}

// ─── Containers (mount once in App root) ─────────────────────────────────────

export default function ModalContainer() {
  const [modal,   setModal]   = useState<ModalPayload | null>(null)
  const [loading, setLoading] = useState<string | null>(null)

  useEffect(() => {
    const mh = (m: ModalPayload | null) => setModal(m)
    const lh = (msg: string | null)     => setLoading(msg)
    modalListeners.push(mh)
    loadingListeners.push(lh)
    return () => {
      modalListeners.splice(modalListeners.indexOf(mh), 1)
      loadingListeners.splice(loadingListeners.indexOf(lh), 1)
    }
  }, [])

  return (
    <>
      {/* Blocking loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex flex-col items-center justify-center gap-4">
          <LoadingSpinner size="lg" />
          <p className="text-white text-sm font-medium">{loading}</p>
        </div>
      )}

      {/* Result modal */}
      {modal && (
        <div
          className="fixed inset-0 bg-black/40 z-[9998] flex items-center justify-center px-4"
          onClick={() => setModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 animate-[fadeScale_0.15s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              {modal.type === 'success'
                ? <CheckCircle size={22} className="text-green-500 shrink-0" />
                : <XCircle    size={22} className="text-red-500   shrink-0" />}
              <h2 className="text-base font-semibold text-gray-800">{modal.title}</h2>
            </div>
            {modal.body && <p className="text-sm text-gray-600 mb-5 pl-9">{modal.body}</p>}
            <div className="flex justify-end">
              <button
                onClick={() => setModal(null)}
                className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors
                  ${modal.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-primary-600'}`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
